/**
 * AppointmentsSection — sección unificada de citas del terapeuta.
 * Reemplaza UpcomingAppointments + AppointmentsListSection (legacy).
 * Muestra en un solo bloque: citas confirmadas/aceptadas + pendientes de aceptar.
 * Diseño: Isabel Moreno (UX/UI Lead) · Implementación: Sofía García (Frontend)
 */
import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useAppointments from 'state/appointments';
import { useSocket } from 'Socket';
import { DARK_TEXT, WARNING_ORANGE } from 'resources/constants/colors';
import { AppointmentCardSkeleton, SkeletonList } from 'components/Skeleton';
import AppointmentCard from './AppointmentCard';

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled.div`
    animation: ${fadeUp} 0.35s ease both;
`;

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 8px;
    padding: 0 2px;
`;

const Badge = styled.span`
    background: ${WARNING_ORANGE};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    border-radius: 10px;
    padding: 1px 7px;
    margin-left: 6px;
    vertical-align: middle;
`;

const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    padding: 12px 16px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 72px;
    justify-content: center;
`;

const Divider = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #bbb;
    margin: 4px 0 2px;
    padding-top: 6px;
    border-top: 1px solid #f0f0f0;
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 16px 10px;
`;

const EmptyText = styled.p`
    font-size: 14px;
    color: ${DARK_TEXT};
    margin: 6px 0 0;
`;

const AppointmentsSection = () => {
    const [appointments, appointmentsDispatcher] = useAppointments();
    const socket = useSocket();

    useEffect(() => {
        appointmentsDispatcher.fetchUpcomingStart();
        appointmentsDispatcher.fetchPendingStart();
    }, []);

    useEffect(() => {
        if (!socket) return;
        socket.off('appointment created').on('appointment created', () => {
            appointmentsDispatcher.fetchPendingStart();
        });
    }, [socket]);

    const isLoading = appointments.fetching.state && Object.keys(appointments.fetching.config).length === 0;
    const upcoming = appointments.upcomingList || [];
    const pending  = appointments.pendingList  || [];

    // Deduplicar: excluir de upcoming cualquier cita que ya está en pending
    // (estado CONFIRMED aparece en ambas queries del backend)
    const pendingIds = new Set(pending.map(p => p.id));
    const upcomingFiltered = upcoming.filter(up => !pendingIds.has(up.id));

    const hasPending = pending.length > 0;
    const isEmpty = upcomingFiltered.length === 0 && pending.length === 0;

    return (
        <Section>
            <SectionLabel>
                Agenda
                {hasPending && <Badge>{pending.length} por aceptar</Badge>}
            </SectionLabel>
            <Card>
                {isLoading ? (
                    <SkeletonList count={2} component={AppointmentCardSkeleton} />
                ) : isEmpty ? (
                    <EmptyState>
                        <span style={{ fontSize: 28 }}>📅</span>
                        <EmptyText>No tienes citas programadas por ahora</EmptyText>
                    </EmptyState>
                ) : (
                    <>
                        {upcomingFiltered.length > 0 && (
                            <>
                                {upcomingFiltered.map(app => (
                                    <AppointmentCard key={`up-${app.id}`} app={app} variant="upcoming" />
                                ))}
                            </>
                        )}
                        {hasPending && (
                            <>
                                {upcomingFiltered.length > 0 && <Divider>Solicitudes nuevas</Divider>}
                                {pending.map(app => (
                                    <AppointmentCard key={`pend-${app.id}`} app={app} variant="pending" />
                                ))}
                            </>
                        )}
                    </>
                )}
            </Card>
        </Section>
    );
};

export default AppointmentsSection;
